import { Button, Form, Label, Message } from "semantic-ui-react";
import {
  ASIN_PATTERN,
  LISTING_PLATFORMS,
  isUnmatchableAmazonListing,
  normalizeAsin,
  normalizeIdentifierInput,
  validatePlatformIdentifiers,
  type ListingPlatform,
  type PlatformIdentifiers,
} from "./listingPayload";

interface PlatformListingFieldsProps {
  platform: ListingPlatform;
  onPlatformChange: (platform: ListingPlatform) => void;
  identifiers: PlatformIdentifiers;
  onIdentifiersChange: (identifiers: PlatformIdentifiers) => void;
  /** Show format errors (the forms turn this on after a submit attempt). */
  showErrors?: boolean;
  disabled?: boolean;
  /** ASINs already linked to the target product (Decision D1). */
  existingAsins?: ReadonlySet<string>;
}

interface IdentifierFieldConfig {
  field: keyof PlatformIdentifiers;
  label: string;
  placeholder: string;
  inputMode?: "numeric";
}

const PLATFORM_FIELDS: Record<ListingPlatform, IdentifierFieldConfig[]> = {
  amazon: [
    { field: "asin", label: "ASIN", placeholder: "Enter ASIN" },
    { field: "msku", label: "MSKU", placeholder: "Enter MSKU" },
  ],
  walmart: [
    { field: "walmartSku", label: "Walmart SKU", placeholder: "Enter Walmart SKU" },
    { field: "walmartItemId", label: "Item ID", placeholder: "Enter Item ID" },
  ],
  shopify: [
    { field: "shopifySku", label: "SKU", placeholder: "Enter SKU" },
    {
      field: "shopifyProductId",
      label: "Product ID",
      placeholder: "Enter Product ID",
      inputMode: "numeric",
    },
  ],
};

/**
 * Platform selector plus the selected platform's optional identifier inputs.
 * Renders Form.Field elements, so mount it inside a semantic-ui <Form>.
 * Build the request body with buildListingPayload from ./listingPayload.
 */
export function PlatformListingFields({
  platform,
  onPlatformChange,
  identifiers,
  onIdentifiersChange,
  showErrors = false,
  disabled = false,
  existingAsins,
}: PlatformListingFieldsProps) {
  const errors = validatePlatformIdentifiers(platform, identifiers);
  const asin = normalizeAsin(identifiers.asin);
  const addsAnotherOffer =
    platform === "amazon" &&
    ASIN_PATTERN.test(asin) &&
    !!existingAsins?.has(asin);

  // No maxLength on the inputs: the browser would cut the end of a pasted
  // value before its leading space could be trimmed. normalizeIdentifierInput
  // trims first, then applies the column's limit.
  const setField = (field: keyof PlatformIdentifiers, value: string) => {
    onIdentifiersChange({
      ...identifiers,
      [field]: normalizeIdentifierInput(field, value),
    });
  };

  return (
    <>
      <Form.Field required>
        <label>Platform</label>
        <Button.Group>
          {LISTING_PLATFORMS.map((option) => (
            <Button
              key={option.value}
              type="button"
              primary={platform === option.value}
              onClick={() => onPlatformChange(option.value)}
              disabled={disabled}
            >
              {option.label}
            </Button>
          ))}
        </Button.Group>
        <p style={{ color: "#666", fontSize: "0.9em", marginTop: "0.5em" }}>
          Identifiers are optional. You can add them later in the grid.
        </p>
      </Form.Field>

      {PLATFORM_FIELDS[platform].map(
        ({ field, label, placeholder, inputMode }) => (
          <Form.Field key={field}>
            <label>{label}</label>
            <Form.Input
              placeholder={placeholder}
              value={identifiers[field]}
              onChange={(e) => setField(field, e.target.value)}
              inputMode={inputMode}
              disabled={disabled}
            />
            {showErrors && errors[field] && (
              <Label basic color="red" pointing>
                {errors[field]}
              </Label>
            )}
            {field === "asin" && addsAnotherOffer && (
              <Label basic color="blue" pointing>
                Adds another offer for this ASIN.
              </Label>
            )}
          </Form.Field>
        )
      )}

      {isUnmatchableAmazonListing(platform, identifiers) && (
        <Message warning visible size="small">
          Without an ASIN or MSKU, this listing can't be matched automatically
          when it goes live on Amazon. Add an MSKU later in the grid.
        </Message>
      )}
    </>
  );
}
